import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { articleApi, scanUrl, auth, qbbAccountApi, submissionApi } from '../../services/api';
import QrModal from './QrModal';
import { AdminHeader, PrimaryBtn, EmptyState } from './AdminLayout';
import { useLightbox } from '../../components/Lightbox';

export default function ArticleList() {
  const { campaignId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variantLoading, setVariantLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [qr, setQr] = useState(null);
  // 发到券宝宝 App（用平台的内部账号，不是扫码路人的号）
  const [pub, setPub] = useState(null);          // 正在配置发布的文章
  const [pubChannel, setPubChannel] = useState('post');
  const [pubCategory, setPubCategory] = useState('ai');
  const [pubAccount, setPubAccount] = useState('');
  const [pubBusy, setPubBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const lb = useLightbox();

  const campaignName = state?.campaignName || '活动';
  const isSuper = auth.isSuperadmin();

  const load = useCallback(() => {
    setLoading(true);
    articleApi.list(campaignId).then(setArticles).finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    submissionApi.categories().then((cs) => setCategories(cs || [])).catch(() => {});
    // 账号池仅超管可读，子账号拿不到就只留「自动轮换」
    qbbAccountApi.list().then((d) => setAccounts((d.list || []).filter((a) => a.status === 'active'))).catch(() => {});
  }, []);

  const openPublish = (article, e) => {
    e.stopPropagation();
    setPub(article);
    setPubChannel(article.qbbChannel || 'post');
    setPubCategory('ai');
    setPubAccount('');
  };

  const doPublish = async () => {
    if (!pub) return;
    setPubBusy(true);
    try {
      const r = await articleApi.publishQbb(pub._id, {
        channel: pubChannel,
        accountId: pubAccount || undefined,
        categoryId: pubChannel === 'post' && pubCategory !== 'ai' ? Number(pubCategory) : undefined,
      });
      if (r.qbbStatus === 'failed') alert('发布失败：' + (r.qbbError || '未知错误'));
      setPub(null);
      load();
    } catch (e) {
      alert(e?.response?.data?.error || '发布失败');
    } finally {
      setPubBusy(false);
    }
  };

  const handleVariant = async (article, e) => {
    e.stopPropagation();
    setVariantLoading(article._id);
    try {
      await articleApi.variant(article._id);
      load();
    } catch {
      alert('变体生成失败，请稍后重试');
    } finally {
      setVariantLoading(null);
    }
  };

  const handleDelete = async (article, e) => {
    e.stopPropagation();
    if (!window.confirm(`确认删除文章「${article.title || '无标题'}」？此操作不可恢复。`)) return;
    setDeleteLoading(article._id);
    try {
      await articleApi.delete(article._id);
      load();
    } catch {
      alert('删除失败，请稍后重试');
    } finally {
      setDeleteLoading(null);
    }
  };

  const pendingCount = articles.filter(a => a.status === 'pending').length;
  const usedCount = articles.filter(a => a.status === 'used').length;

  return (
    <div style={styles.page}>
      <AdminHeader
        title={campaignName}
        back={true}
        actions={
          <PrimaryBtn onClick={() => navigate(`/admin/campaign/${campaignId}/article/new`, { state: { campaignName, campaignId } })}>
            新建文章
          </PrimaryBtn>
        }
      />

      {!loading && articles.length > 0 && (
        <div style={styles.summary}>
          <div style={styles.summaryItem}>
            <span style={styles.summaryNum}>{articles.length}</span>
            <span style={styles.summaryLabel}>总数</span>
          </div>
          <div style={styles.summaryDiv} />
          <div style={styles.summaryItem}>
            <span style={{ ...styles.summaryNum, color: '#22c55e' }}>{pendingCount}</span>
            <span style={styles.summaryLabel}>待用</span>
          </div>
          <div style={styles.summaryDiv} />
          <div style={styles.summaryItem}>
            <span style={{ ...styles.summaryNum, color: '#9ca3af' }}>{usedCount}</span>
            <span style={styles.summaryLabel}>已用</span>
          </div>
        </div>
      )}

      <div style={styles.body}>
        {loading ? (
          <div style={styles.skeletons}>
            {[1, 2, 3, 4].map(i => <div key={i} style={styles.skeleton} />)}
          </div>
        ) : articles.length === 0 ? (
          <EmptyState text="暂无文章，点击右上角新建" />
        ) : (
          <div style={styles.list}>
            {articles.map((a) => (
              <div
                key={a._id}
                style={styles.card}
                onClick={() => navigate(`/admin/article/${a._id}`, { state: { campaignId, campaignName } })}
              >
                <div style={styles.thumbWrap}>
                  {(a.images || []).filter(Boolean).length > 0 ? (
                    <img
                      src={(a.images || []).filter(Boolean)[0]}
                      alt=""
                      style={styles.thumbImg}
                      onClick={(e) => { e.stopPropagation(); lb.open((a.images || []).filter(Boolean), 0); }}
                      onError={(e) => { e.target.style.visibility = 'hidden'; }}
                    />
                  ) : (
                    <span style={styles.thumbEmpty}>无图</span>
                  )}
                  <span style={styles.thumbDot(a.status)} />
                </div>
                <div style={styles.cardBody}>
                  <div style={styles.cardTitle}>{a.title || '（无标题）'}</div>
                  <div style={styles.metaRow}>
                    <span style={styles.statusLabel(a.status)}>
                      {a.status === 'pending' ? '待用' : '已用'}
                    </span>
                    <span style={styles.scanCount}>扫码 {a.scanCount} 次</span>
                    {!a.isOriginal && <span style={styles.variantTag}>变体</span>}
                    {a.qbbStatus === 'sent' && (
                      <span style={styles.qbbTag}>已发券宝宝{a.qbbChannel === 'review' ? '评价' : '帖子'}</span>
                    )}
                    {a.qbbStatus === 'failed' && <span style={styles.qbbFailTag}>券宝宝失败</span>}
                  </div>
                </div>
                <div style={styles.cardActions}>
                  <button
                    style={styles.qrBtnXhs}
                    title="小红书二维码"
                    onClick={(e) => { e.stopPropagation(); setQr({ url: scanUrl.article(a._id), title: `${a.title} — 小红书` }); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 14H9V8h2v8zm4 0h-2V8h2v8z"/></svg>
                  </button>
                  <button
                    style={styles.qrBtnGoogle}
                    title="Google Review 二维码"
                    onClick={(e) => { e.stopPropagation(); setQr({ url: scanUrl.articleGoogle(a._id), title: `${a.title} — Google Review` }); }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12.24 10.285V14.4h6.806c-.275 1.765-2.056 5.174-6.806 5.174-4.095 0-7.439-3.389-7.439-7.574s3.345-7.574 7.439-7.574c2.33 0 3.891.989 4.785 1.849l3.254-3.138C18.189 1.186 15.479 0 12.24 0c-6.635 0-12 5.365-12 12s5.365 12 12 12c6.926 0 11.52-4.869 11.52-11.726 0-.788-.085-1.39-.189-1.989H12.24z"/></svg>
                  </button>
                  <button
                    style={styles.qbbBtn}
                    onClick={(e) => openPublish(a, e)}
                  >
                    {a.qbbStatus === 'sent' ? '再发券宝宝' : '发券宝宝'}
                  </button>
                  <button
                    style={variantLoading === a._id ? styles.varBtnLoading : styles.varBtn}
                    disabled={variantLoading === a._id}
                    onClick={(e) => handleVariant(a, e)}
                  >
                    {variantLoading === a._id ? '...' : '变体'}
                  </button>
                  {isSuper && (
                    <button
                      style={deleteLoading === a._id ? styles.delBtnLoading : styles.delBtn}
                      disabled={deleteLoading === a._id}
                      onClick={(e) => handleDelete(a, e)}
                    >
                      {deleteLoading === a._id ? '...' : '删除'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {lb.node}

      {pub && (
        <div style={styles.overlay} onClick={() => setPub(null)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <p style={styles.modalTitle}>发到券宝宝</p>
            <p style={styles.modalLabel}>去向</p>
            <div style={styles.segRow}>
              <span
                style={{ ...styles.seg, ...(pubChannel === 'post' ? styles.segOn : {}) }}
                onClick={() => setPubChannel('post')}
              >探索帖子（原文）</span>
              <span
                style={{ ...styles.seg, ...(pubChannel === 'review' ? styles.segOn : {}) }}
                onClick={() => setPubChannel('review')}
              >店铺评价（AI 缩写）</span>
            </div>

            {pubChannel === 'post' && (
              <>
                <p style={styles.modalLabel}>分类</p>
                <select style={styles.select} value={pubCategory} onChange={(e) => setPubCategory(e.target.value)}>
                  <option value="ai">AI 智能选择</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </>
            )}

            <p style={styles.modalLabel}>用哪个内部账号</p>
            <select style={styles.select} value={pubAccount} onChange={(e) => setPubAccount(e.target.value)}>
              <option value="">自动轮换（最久没用的）</option>
              {accounts.map((a) => (
                <option key={a._id} value={a._id}>{a.phone}{a.nickname ? ` · ${a.nickname}` : ''}</option>
              ))}
            </select>

            <p style={styles.modalHint}>
              {pubChannel === 'review'
                ? '发到这篇文章所属商家的店铺评价下，正文会先 AI 缩写成短评价。'
                : '发到券宝宝探索页，正文用文章原文，并关联该商家店铺。'}
            </p>

            <div style={styles.modalActions}>
              <button style={styles.cancelBtn} onClick={() => setPub(null)}>取消</button>
              <button style={pubBusy ? styles.okBtnBusy : styles.okBtn} disabled={pubBusy} onClick={doPublish}>
                {pubBusy ? '发送中...' : '确认发布'}
              </button>
            </div>
          </div>
        </div>
      )}

      {qr && <QrModal url={qr.url} title={qr.title} onClose={() => setQr(null)} />}
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: '#f7f7f8', display: 'flex', flexDirection: 'column' },
  summary: {
    display: 'flex', alignItems: 'center', background: '#fff',
    borderBottom: '1px solid #f0f0f0', padding: '10px 0',
  },
  summaryItem: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 },
  summaryNum: { fontSize: 22, fontWeight: 700, color: '#111' },
  summaryLabel: { fontSize: 11, color: '#9ca3af' },
  summaryDiv: { width: 1, height: 28, background: '#e5e7eb' },
  body: { flex: 1, padding: '12px 12px 24px' },
  skeletons: { display: 'flex', flexDirection: 'column', gap: 8 },
  skeleton: { height: 68, borderRadius: 10, background: '#efefef' },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: {
    background: '#fff', borderRadius: 10, padding: '12px 14px',
    display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
    boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  },
  thumbWrap: {
    position: 'relative', width: 52, height: 52, borderRadius: 8, flexShrink: 0,
    background: '#f0f0f0', overflow: 'hidden',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover' },
  thumbEmpty: { fontSize: 11, color: '#bbb' },
  thumbDot: (status) => ({
    position: 'absolute', top: 4, left: 4, width: 9, height: 9, borderRadius: '50%',
    border: '1.5px solid #fff', boxSizing: 'border-box',
    background: status === 'pending' ? '#22c55e' : '#d1d5db',
  }),
  cardBody: { flex: 1, minWidth: 0 },
  cardTitle: {
    fontSize: 15, fontWeight: 600, color: '#111', marginBottom: 5,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  metaRow: { display: 'flex', alignItems: 'center', gap: 8 },
  statusLabel: (status) => ({
    fontSize: 11, fontWeight: 600,
    color: status === 'pending' ? '#16a34a' : '#9ca3af',
  }),
  scanCount: { fontSize: 11, color: '#9ca3af' },
  variantTag: {
    fontSize: 10, color: '#6366f1', background: '#eef2ff',
    padding: '1px 6px', borderRadius: 4, fontWeight: 600,
  },
  qbbTag: {
    fontSize: 10, color: '#16a34a', background: '#f0fdf4',
    padding: '1px 6px', borderRadius: 4, fontWeight: 600,
  },
  qbbFailTag: {
    fontSize: 10, color: '#ef4444', background: '#fef2f2',
    padding: '1px 6px', borderRadius: 4, fontWeight: 600,
  },
  cardActions: { display: 'flex', gap: 6, flexShrink: 0 },
  qrBtnXhs: {
    width: 32, height: 32, border: 'none', borderRadius: 6,
    background: '#FA1C51', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  qrBtnGoogle: {
    width: 32, height: 32, border: 'none', borderRadius: 6,
    background: '#4285f4', color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  varBtn: {
    padding: '0 12px', height: 32, background: '#FA1C51', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  varBtnLoading: {
    padding: '0 12px', height: 32, background: '#fca5a5', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'default', fontSize: 12, fontWeight: 600,
  },
  qbbBtn: {
    padding: '0 10px', height: 32, background: '#fff', color: '#FA1C51',
    border: '1px solid #fecdd3', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, zIndex: 50,
  },
  modal: { background: '#fff', borderRadius: 12, padding: 18, width: '100%', maxWidth: 340 },
  modalTitle: { margin: '0 0 12px', fontSize: 16, fontWeight: 600, color: '#111' },
  modalLabel: { margin: '10px 0 6px', fontSize: 12, color: '#6b7280' },
  modalHint: { margin: '12px 0 0', fontSize: 11, color: '#9ca3af', lineHeight: 1.6 },
  segRow: { display: 'flex', gap: 8 },
  seg: {
    flex: 1, textAlign: 'center', padding: '9px 0', borderRadius: 8, cursor: 'pointer',
    border: '1.5px solid #e5e7eb', fontSize: 12, color: '#6b7280',
  },
  segOn: { borderColor: '#FA1C51', color: '#FA1C51', fontWeight: 600 },
  select: {
    width: '100%', boxSizing: 'border-box', padding: '10px 12px',
    border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff',
  },
  modalActions: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 },
  cancelBtn: {
    padding: '0 14px', height: 34, background: '#fff', color: '#6b7280',
    border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'pointer', fontSize: 13,
  },
  okBtn: {
    padding: '0 14px', height: 34, background: '#FA1C51', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
  },
  okBtnBusy: {
    padding: '0 14px', height: 34, background: '#fca5a5', color: '#fff',
    border: 'none', borderRadius: 6, cursor: 'default', fontSize: 13, fontWeight: 600,
  },
  delBtn: {
    padding: '0 12px', height: 32, background: '#fff', color: '#ef4444',
    border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
  },
  delBtnLoading: {
    padding: '0 12px', height: 32, background: '#fff', color: '#fca5a5',
    border: '1px solid #fee2e2', borderRadius: 6, cursor: 'default', fontSize: 12, fontWeight: 600,
  },
};
